import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Textarea } from '../../../components/ui/textarea';
import { ArrowLeft, Save, MapPin, Building2, User, Mail, Phone, Loader2 } from 'lucide-react';
import { toast } from 'react-toastify';
import { useApi } from '../../../hooks/useApi';

const labelClassName = 'text-sm font-medium text-foreground';
const fieldClassName =
  'h-11 rounded-lg border-1 border-muted-foreground/20 bg-background text-foreground shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:ring-2 focus-visible:ring-primary/25 focus-visible:border-primary/50';
const selectTriggerClassName =
  'h-11 rounded-lg border-1 border-muted-foreground/20 bg-background text-foreground shadow-sm focus:ring-2 focus:ring-primary/25 focus:border-primary/50';

export default function AddAccount() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [selectedStateId, setSelectedStateId] = useState('');
  const [isLoadingStates, setIsLoadingStates] = useState(false);
  const [isLoadingCities, setIsLoadingCities] = useState(false);
  const { post, get } = useApi();
  
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors }
  } = useForm({
    defaultValues: {
      name: '',
      email: '',
      phone: '',
      gstNumber: '',
      status: 'active',
      password: '',
      house_no: '',
      street: '',
      sub_area: '',
      area: '',
      landmark: '',
      state_id: '',
      city_id: '',
      country: 91,
      pincode: ''
    }
  });

  React.useEffect(() => {
    register('state_id', { required: 'State is required' });
    register('city_id', { required: 'City is required' });
  }, [register]);

  const extractList = (response) => {
    if (response?.success && Array.isArray(response?.data)) return response.data;
    if (Array.isArray(response?.data)) return response.data;
    if (Array.isArray(response)) return response;
    return [];
  };

  const fetchAllPages = async (urlBuilder) => {
    let page = 1;
    let lastPage = 1;
    const all = [];

    do {
      const response = await get(urlBuilder(page));
      const batch = extractList(response);
      all.push(...batch);
      lastPage =
        response?.meta?.lastPage ||
        response?.meta?.last_page ||
        response?.last_page ||
        1;
      if (batch.length === 0) break;
      page += 1;
    } while (page <= lastPage);

    return all;
  };

  // Fetch states on component mount
  React.useEffect(() => {
    fetchStates();
  }, []);

  // Fetch cities when state is selected
  React.useEffect(() => {
    if (selectedStateId) {
      fetchCities(selectedStateId);
    } else {
      setCities([]);
      setValue('city_id', '');
    }
  }, [selectedStateId, setValue]);

  const fetchStates = async () => {
    try {
      setIsLoadingStates(true);
      const statesData = await fetchAllPages(
        (page) => `/states?per_page=1000&page=${page}`
      );
      setStates(statesData);
    } catch (error) {
      toast.error('Failed to load states');
      setStates([]);
    } finally {
      setIsLoadingStates(false);
    }
  };

  const fetchCities = async (stateId) => {
    try {
      setIsLoadingCities(true);
      const citiesData = await fetchAllPages(
        (page) => `/${stateId}/city?per_page=1000&page=${page}`
      );
      setCities(citiesData);
    } catch (error) {
      toast.error('Failed to load cities');
      setCities([]);
    } finally {
      setIsLoadingCities(false);
    }
  };

  const handleStateChange = (stateId) => {
    setSelectedStateId(stateId);
    setValue('state_id', stateId, { shouldValidate: true });
    setValue('city_id', '', { shouldValidate: true });
  };

  const onSubmit = async (data) => {
    setIsLoading(true);
    try {
      // Convert string values to appropriate types and map field names
      const payload = {
        ...data,
        gst_number: data.gstNumber, // Map gstNumber to gst_number for API
        country: parseInt(data.country),
        pincode: parseInt(data.pincode),
      };
      
      // Remove the original gstNumber field since we mapped it to gst_number
      delete payload.gstNumber;


      const response = await post('/accounts', payload);


      toast.success('Account created successfully!');
      navigate('/admin/accounts');
    } catch (error) {
      
      let errorMessage = 'An error occurred while creating the account. Please try again.';
      
      if (error.response?.status === 500) {
        errorMessage = 'Server error (500). The server encountered an unexpected condition.';
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="relative w-full max-w-4xl mx-auto">
      <button
        type="button"
        className="mb-3 flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground lg:hidden"
        onClick={() => navigate('/admin/accounts')}
      >
        <ArrowLeft className="h-4 w-4" />
        Back to accounts
      </button>

      <div className="mb-4 sm:mb-6">
        <h1 className="text-lg font-bold text-zinc-900 sm:text-xl dark:text-zinc-100">Add New Account</h1>
        <p className="mt-0.5 text-[12px] text-muted-foreground sm:text-sm">
          Create a new account for TrackYourWheelz
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 sm:space-y-6">
        {/* Company Information */}
        <Card className="border-1 border-muted-foreground/20 bg-card shadow-sm">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-xl">
              <Building2 className="w-5 h-5" />
              Company Information
            </CardTitle>
            <CardDescription>
              Basic company details and contact information
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4 ">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <div className="space-y-2">
                <Label htmlFor="name" className={labelClassName}>Company Name </Label>
                <Input
                  id="name"
                  {...register('name', { required: 'Company name is required' })}
                  placeholder="Enter company name"
                  className={fieldClassName}
                />
                {errors.name && (
                  <p className="text-sm text-red-500">{errors.name.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="email" className={labelClassName}>Email </Label>
                <Input
                  id="email"
                  type="email"
                  className={fieldClassName}
                  {...register('email', { 
                    required: 'Email is required',
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: 'Invalid email address'
                      
                    }
                  })}
                  placeholder="Enter email address"
                />
                {errors.email && (
                  <p className="text-sm text-red-500">{errors.email.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone" className={labelClassName}>Phone Number </Label>
                <Input
                  id="phone"
                  className={fieldClassName}
                  {...register('phone', { 
                    required: 'Phone number is required',
                    pattern: {
                      value: /^[0-9]{10}$/,
                      message: 'Phone number must be 10 digits'
                    }
                  })}
                  placeholder="Enter phone number"
                />
                {errors.phone && (
                  <p className="text-sm text-red-500">{errors.phone.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="gstNumber" className={labelClassName}>GST Number</Label>
                <Input
                  id="gstNumber"
                  className={fieldClassName}
                  {...register('gstNumber', { 
                    pattern: {
                      value: /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/,
                      message: 'Invalid GST number format'
                    }
                  })}
                  placeholder="Enter GST number (optional)"
                />
                {errors.gstNumber && (
                  <p className="text-sm text-red-500">{errors.gstNumber.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="status" className={labelClassName}>Status </Label>
                <Select onValueChange={(value) => setValue('status', value)} value={watch('status')} defaultValue="active">
                  <SelectTrigger className={selectTriggerClassName}>
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent className="z-[9999] max-h-[min(70vh,20rem)]">
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                    <SelectItem value="suspended">Suspended</SelectItem>
                  </SelectContent>
                </Select>
                {errors.status && (
                  <p className="text-sm text-red-500">{errors.status.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className={labelClassName}>Password </Label>
                <Input
                  id="password"
                  type="password"
                  className={fieldClassName}
                  {...register('password', { 
                    required: 'Password is required',
                    pattern: {
                      value: /^[A-Za-z0-9]{8,}$/,
                      message: 'Password must be at least 8 characters long'
                    }
                  })}
                  placeholder="Enter password"
                />
                {errors.password && (
                  <p className="text-sm text-red-500">{errors.password.message}</p>
                )}
              </div>
              
            </div>
          </CardContent>
        </Card>

        {/* Address Information */}
        <Card className="border-1 border-muted-foreground/20 bg-card shadow-sm">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-xl">
              <MapPin className="w-5 h-5" />
              Address Information
            </CardTitle>
            <CardDescription>
              Complete address details and location information
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
              <div className="space-y-2">
                <Label htmlFor="house_no" className={labelClassName}>House Number </Label>
                <Input
                  id="house_no"
                  {...register('house_no', { required: 'House number is required' })}
                  placeholder="Enter house number"
                  className={fieldClassName}
                />
                {errors.house_no && (
                  <p className="text-sm text-red-500">{errors.house_no.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="street" className={labelClassName}>Street </Label>
                <Input
                  id="street"
                  {...register('street', { required: 'Street is required' })}
                  placeholder="Enter street name"
                  className={fieldClassName}
                />
                {errors.street && (
                  <p className="text-sm text-red-500">{errors.street.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="sub_area" className={labelClassName}>Sub Area </Label>
                <Input
                  id="sub_area"
                  {...register('sub_area', { required: 'Sub area is required' })}
                  placeholder="Enter sub area"
                  className={fieldClassName}
                />
                {errors.sub_area && (
                  <p className="text-sm text-red-500">{errors.sub_area.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="area" className={labelClassName}>Area </Label>
                <Input
                  id="area"
                  {...register('area', { required: 'Area is required' })}
                  placeholder="Enter area"
                  className={fieldClassName}
                />
                {errors.area && (
                  <p className="text-sm text-red-500">{errors.area.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="landmark" className={labelClassName}>Landmark</Label>
                <Input
                  id="landmark"
                  {...register('landmark')}
                  placeholder="Enter landmark (optional)"
                  className={fieldClassName}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="pincode" className={labelClassName}>Pincode </Label>
                <Input
                  id="pincode"
                  type="number"
                  {...register('pincode', { 
                    required: 'Pincode is required',
                    min: { value: 100000, message: 'Invalid pincode' },
                    max: { value: 999999, message: 'Invalid pincode' }
                  })}
                  placeholder="Enter pincode"
                  className={fieldClassName}
                />
                {errors.pincode && (
                  <p className="text-sm text-red-500">{errors.pincode.message}</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="state_id" className={labelClassName}>State </Label>
                <Select onValueChange={handleStateChange} disabled={isLoadingStates} value={watch('state_id') || undefined}>
                  <SelectTrigger className={selectTriggerClassName}>
                    <SelectValue placeholder={isLoadingStates ? "Loading states..." : "Select state"} />
                  </SelectTrigger>
                  <SelectContent className="z-[9999] max-h-[min(70vh,20rem)] overflow-y-auto">
                    {states.length === 0 && !isLoadingStates ? (
                      <div className="px-2 py-1.5 text-sm text-muted-foreground">No states found</div>
                    ) : (
                      states.map((state) => (
                        <SelectItem key={state.id || state._id} value={String(state.id || state._id)}>
                          {state.name || state.state_name || state.title || state.label || `State ${state.id}`}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
                {errors.state_id && (
                  <p className="text-sm text-red-500">{errors.state_id.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="city_id" className={labelClassName}>City </Label>
                <Select 
                  onValueChange={(value) => setValue('city_id', value, { shouldValidate: true })} 
                  disabled={!selectedStateId || isLoadingCities}
                  value={watch('city_id')}
                >
                  <SelectTrigger className={selectTriggerClassName}>
                    <SelectValue placeholder={
                      isLoadingCities ? "Loading cities..." :
                      selectedStateId ? "Select city" : 
                      "Select state first"
                    } />
                  </SelectTrigger>
                  <SelectContent className="z-[9999] max-h-[min(70vh,20rem)] overflow-y-auto">
                    {cities.length === 0 && !isLoadingCities ? (
                      <div className="px-2 py-1.5 text-sm text-muted-foreground">
                        {selectedStateId ? 'No cities found' : 'Select a state first'}
                      </div>
                    ) : (
                      cities.map((city) => (
                        <SelectItem key={city.id || city._id} value={String(city.id || city._id)}>
                          {city.cityname || city.name || city.city_name || city.title || city.label || `City ${city.id}`}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
                {errors.city_id && (
                  <p className="text-sm text-red-500">{errors.city_id.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="country" className={labelClassName}>Country Code </Label>
                <Input
                  id="country"
                  type="number"
                  {...register('country', { required: 'Country code is required' })}
                  placeholder="91"
                  defaultValue={91}
                  className={fieldClassName}
                />
                {errors.country && (
                  <p className="text-sm text-red-500">{errors.country.message}</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Submit Button */}
        <div className="flex justify-end gap-4">
          <Button
            type="button"
            variant="outline"
            className='border-muted-foreground/10 border-1 '
            onClick={() => navigate('/admin/accounts')}
          >
            Cancel
          </Button>
          <Button type="submit" disabled={isLoading} variant="outline" className='border-muted-foreground/10 border-1'>
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Creating...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Create Account
              </>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
}
